
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Container from '../ui/Container';
import Section from '../ui/Section';

export default function Hero() {
  const { t } = useTranslation();

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Section 
      id="home" 
      className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20 md:pt-16 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700"
    >
      {/* Background decoration matching footer */}
      <div
        className={`
          absolute inset-0 
          bg-[url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]
          opacity-50
        `}
      ></div>
      
      {/* Animated elements */}
      <div className="hidden md:block absolute top-32 left-16 w-2 h-2 bg-white/40 rounded-full animate-pulse"></div>
      <div className="hidden md:block absolute top-24 right-24 w-3 h-3 bg-white/50 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="hidden md:block absolute bottom-40 left-32 w-2 h-2 bg-white/30 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="hidden md:block absolute bottom-28 right-16 w-4 h-4 bg-white/40 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      
      {/* Additional small bubbles for better distribution */}
      <div className="hidden lg:block absolute top-1/2 left-8 w-1.5 h-1.5 bg-white/30 rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
      <div className="hidden lg:block absolute top-3/4 right-8 w-2.5 h-2.5 bg-white/40 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      
      <Container className="relative z-10 text-center">
        <div className="max-w-4xl mx-auto animate-fade-in">
          {/* Main Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 md:mb-8 font-poppins leading-tight drop-shadow-lg">
            {t('hero.title')}
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-blue-100 mb-8 md:mb-12 font-inter leading-relaxed max-w-3xl mx-auto px-4">
            {t('hero.subtitle')}
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center px-4">
            <Button 
              size="lg" 
              onClick={() => scrollToSection('#contact')}
              className="shadow-colored bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 md:px-12 py-3 md:py-4 text-base md:text-lg font-semibold transform hover:scale-105 transition-all duration-300 w-full sm:w-auto whitespace-nowrap"
            >
              {t('hero.cta')}
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={() => scrollToSection('#services')}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-700 px-8 md:px-12 py-3 md:py-4 text-base md:text-lg font-semibold transform hover:scale-105 transition-all duration-300 w-full sm:w-auto whitespace-nowrap"
            >
              {t('hero.learnMore')}
            </Button>
          </div>
        </div>
        
        {/* Floating Tech Icons - Repositioned to avoid overlaps */}
        <div className="hidden xl:flex absolute top-20 left-20 w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full shadow-lg border border-white/20 items-center justify-center animate-bounce-gentle">
          <i className="ri-code-s-slash-line text-white text-2xl"></i>
        </div>
        <div className="hidden xl:flex absolute top-16 right-20 w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full shadow-lg border border-white/20 items-center justify-center animate-bounce-gentle" style={{ animationDelay: '1s' }}>
          <i className="ri-rocket-line text-white text-3xl"></i>
        </div>
        <div className="hidden xl:flex absolute bottom-32 left-16 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full shadow-lg border border-white/20 items-center justify-center animate-bounce-gentle" style={{ animationDelay: '2s' }}>
          <i className="ri-settings-3-line text-white text-xl"></i>
        </div>
        <div className="hidden xl:flex absolute bottom-20 right-32 w-14 h-14 bg-white/10 backdrop-blur-sm rounded-full shadow-lg border border-white/20 items-center justify-center animate-bounce-gentle" style={{ animationDelay: '0.5s' }}>
          <i className="ri-database-2-line text-white text-xl"></i>
        </div>
      </Container>
    </Section>
  );
}
